// RedeemedHistory.tsx
import { fetchPointsHistory } from '@/api/account';
import { IPointsHistoryItem } from '@/api/types/account';
import Dialog from '@/components/Dialog';
import Popup from '@/components/Popup';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import Tabs, { TabItemProps } from '@/components/Tabs';
import { Box } from '@mui/material';

import InfiniteScroll from '@/components/InfiniteScroll';
import { colors, font } from '@/theme/zeelool.theme';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { generateRegexBasedKey } from '@/utils/tools';
import { useTranslations } from '@/i18n/use-translations';
import React, { useContext, useEffect, useState } from 'react';

interface IRedeemedHistoryProps {
  show: boolean;
  onClose: () => void;
}

const RedeemedHistory: React.FC<IRedeemedHistoryProps> = ({ show, onClose }) => {
  const [historyList, setHistoryList] = useState<IPointsHistoryItem[]>([]);
  const { deviceType } = useContext(ResponsiveContext);
  const t = useTranslations('account.pointsMall.redeemedHistory');

  const [activeTab, setActiveTab] = useState<string>('0');
  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show) {
      getHistoryList();
    } else {
      setPage(1);
      setTotal(0);
      setActiveTab('0');
    }
  }, [show, page, activeTab]);

  const getHistoryList = async () => {
    setLoading(true);
    if (page === 1) {
      setHistoryList([]);
    }
    try {
      const {
        data: { data: dataList, total: totalCount },
      } = await fetchPointsHistory({
        page,
        limit,
        way: Number(activeTab) === 0 ? null : Number(activeTab),
      });
      setHistoryList((prevList) => [...prevList, ...(dataList as IPointsHistoryItem[])]);
      setTotal(totalCount);
    } catch (error) {
      logger.error(error);
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    if (historyList.length > 0) {
      return (
        <>
          <ul>
            {historyList.map((item) => (
              <li
                key={generateRegexBasedKey()}
                className={`flex justify-between items-center border-b ${deviceType === EPlatform.WEB ? 'h-[78px]  border-auxiliary-light-gray' : 'h-[68px]  border-[#d4d4d4]'}`}
              >
                <div>
                  <p className="text-14 font-bb">{item.content}</p>
                  <p
                    className={`mt-8 text-auxiliary-text ${deviceType === EPlatform.WEB ? 'text-14' : 'text-12'}`}
                  >
                    {item.created_at_show}
                  </p>
                </div>
                <p
                  className={`font-bb ${item.way === 1 ? `text-warning` : ''} ${deviceType === EPlatform.WEB ? 'text-16' : 'text-14'}`}
                >
                  {item.value}
                </p>
              </li>
            ))}
          </ul>
        </>
      );
    }
    if (!loading && historyList.length === 0) {
      return (
        <div
          className={`flex flex-col items-center box-border ${
            deviceType === EPlatform.WEB ? 'pt-[120px]' : 'pt-[105px]'
          }`}
        >
          {/* <Image
            src={`/account/redeemable-items/null-img.png`}
            alt="No data"
            width={deviceType === EPlatform.WEB ? 168 : 84}
            height={deviceType === EPlatform.WEB ? 168 : 84}
          /> */}
          <p className="text-16 font-bb">{t('noPoints')}</p>
        </div>
      );
    }
    if (loading && historyList.length === 0) {
      return (
        <>
          <div className={`${deviceType === EPlatform.WEB ? 'h-[130px]' : 'h-[170px]'}`} />
        </>
      );
    }
  };
  const tabItems: TabItemProps[] = [
    {
      key: '0',
      label: t('tabs.viewAll'),
    },
    {
      key: '1',
      label: t('tabs.income'),
    },
    {
      key: '2',
      label: t('tabs.expenditure'),
    },
  ];
  return (
    <>
      <WebOnly>
        <Dialog
          open={show}
          maxWidth="lg"
          onClose={onClose}
          title={t('title')}
          footer={null}
          PaperProps={{
            style: {
              maxHeight: '80vh',
              minHeight: '460px',
            },
          }}
          sx={{ '& .MuiDialogContent-root': { height: '85vh' } }}
        >
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 100,
              backgroundColor: 'white',
              width: '100%',
            }}
          >
            <Tabs
              items={tabItems}
              activeKey={activeTab}
              onChange={(key) => {
                setPage(1);
                setActiveTab(key);
                setHistoryList([]);
              }}
              showLine
              hasTabContent={false}
              sx={{
                '& .MuiTabs-flexContainer': {
                  justifyContent: 'center',
                },
                '& .MuiTab-root': {
                  color: colors.auxiliaryDarkGray,
                },
                '& .Mui-selected': {
                  color: `${colors.primaryText} !important`,
                  fontFamily: font.bb,
                },
                '& .MuiTabs-list': {
                  gap: 5,
                },
              }}
            />
          </Box>
          <div className="history-list">{renderContent()}</div>
          <InfiniteScroll
            onReachBottom={() => {
              const newPage = page + 1;
              setPage(newPage);
            }}
            loading={loading}
            page={page}
            total={total}
            limit={limit}
          />
        </Dialog>
      </WebOnly>
      <WapOnly>
        <Popup
          open={show}
          onClose={onClose}
          position="bottom"
          height="76vh"
          title={t('title')}
          showDivider={false}
          sx={{ '& .popup-content': { pt: 0 } }}
        >
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 100,
              backgroundColor: 'white',
              width: '100%',
              '& .MuiTabs-root': {
                width: '100%',
              },
            }}
          >
            <Tabs
              items={tabItems}
              activeKey={activeTab}
              onChange={(key) => {
                setPage(1);
                setActiveTab(key);
                setHistoryList([]);
              }}
              showLine
              hasTabContent={false}
              sx={{
                '& .MuiTabs-flexContainer': {
                  justifyContent: 'center',
                },
                '& .MuiTab-root': {
                  color: colors.auxiliaryDarkGray,
                },
                '& .Mui-selected': {
                  color: `${colors.primaryText} !important`,
                  fontFamily: font.bb,
                },
                '& .MuiTabs-list': {
                  justifyContent: 'space-between',
                },
              }}
            />
          </Box>
          <div className="history-list">{renderContent()}</div>
          <InfiniteScroll
            onReachBottom={() => {
              const newPage = page + 1;
              setPage(newPage);
            }}
            loading={loading}
            page={page}
            total={total}
            limit={limit}
          />
        </Popup>
      </WapOnly>
    </>
  );
};

export default RedeemedHistory;
